import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl } from "../Utils/api";

// Initial state
const initialState = {
  renewalQuotes: {
    data: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    loading: false,
    error: null
  },
  quoteDetails: {
    data: null,
    loading: false,
    error: null
  },
  quoteAction: {
    loading: false,
    success: false,
    error: null
  },
  rejectedQuoteData: null // Store rejected quote data for form population
};

// Helper function to extract data from response
const extractResponseData = (response) => {
  console.log("API Raw response:", response);
  
  if (!response) {
    console.error("Empty response from API");
    return { data: [] };
  }
  
  // If response is already the data array
  if (Array.isArray(response)) {
    console.log("Response is an array with length:", response.length);
    return { 
      data: response,
      totalElements: response.length,
      totalPages: 1,
      number: 0
    };
  }
  
  // If response has content property (pagination format)
  if (response.content && Array.isArray(response.content)) {
    console.log("Response has content array with length:", response.content.length);
    return response;
  }
  
  // If response has data property
  if (response.data) {
    console.log("Response has data property");
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        totalElements: response.data.length,
        totalPages: 1,
        number: 0
      };
    }
    
    if (response.data.content && Array.isArray(response.data.content)) {
      return response.data;
    }
  }
  
  // If response has results or result property
  if (response.results && Array.isArray(response.results)) {
    console.log("Response has results array");
    return {
      data: response.results,
      totalElements: response.results.length,
      totalPages: 1,
      number: 0
    };
  }
  
  if (response.result && Array.isArray(response.result)) {
    console.log("Response has result array");
    return {
      data: response.result,
      totalElements: response.result.length,
      totalPages: 1,
      number: 0
    };
  }
  
  // Last resort - try to find any array property
  const arrayProps = Object.keys(response).filter(key => 
    Array.isArray(response[key]) && response[key].length > 0
  );
  
  if (arrayProps.length > 0) {
    console.log("Found array property:", arrayProps[0]);
    return {
      data: response[arrayProps[0]],
      totalElements: response[arrayProps[0]].length,
      totalPages: 1,
      number: 0
    };
  }
  
  return { data: [] };
};

// Update the getAllRenewalQuotes function to handle all the filter parameters
export const getAllRenewalQuotes = createAsyncThunk(
  "getAllRenewalQuotes", 
  async (params = {}, { rejectWithValue }) => {
    try {
      // Convert params object to URL query string
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      queryParams.append('page', params.page || 0);
      queryParams.append('size', params.size || 10);
      
      // Add filter parameters if they exist
      if (params.status) {
        queryParams.append('status', params.status);
      }
      
      if (params.startDate) {
        queryParams.append('startDate', params.startDate);
      }
      
      if (params.endDate) {
        queryParams.append('endDate', params.endDate);
      }
      
      if (params.minPremium) {
        queryParams.append('minPremium', params.minPremium);
      }
      
      if (params.maxPremium) {
        queryParams.append('maxPremium', params.maxPremium);
      }
      
      if (params.lossRatio) {
        queryParams.append('lossRatio', params.lossRatio);
      }
      
      if (params.search) {
        queryParams.append('search', params.search);
      }
      
      const token = localStorage.getItem("auth-token");
      const response = await axios({
        method: "GET",
        url: `${baseUrl}/quotation/renewal?${queryParams.toString()}`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      return response;
    } catch (error) {
      console.error("Error fetching renewal quotes:", error);
      return rejectWithValue(error.response?.data?.message || "Failed to fetch renewal quotes");
    }
  }
);

// Get renewal quote details
export const getRenewalQuoteDetails = createAsyncThunk(
  "renewal/getRenewalQuoteDetails",
  async (quoteId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("auth-token");
      const response = await axios({
        method: "GET",
        url: `${baseUrl}/quotation/renewal/${quoteId}`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      
      // For development - return mock data if the API is not ready
      // Comment this out when the real API is working
      /*
      return {
        id: quoteId,
        client_name: "Acme Corporation",
        member_id: "QT-2025-001",
        expiry_date: "May 15, 2025",
        last_modified: new Date().toLocaleDateString(),
        policy_start_date: "01/06/2024",
        policy_end_date: "31/05/2025",
        report_date: "29/04/2025",
        days_on_cover: 333,
        annual_premium: 131969250,
        earned_premium: 120399343,
        claims_incurred: 100599663,
        expected_claims: 110266898,
        previous_premium: 8000000,
        endorsements: 40000,
        current_premium: 880000,
        estimated_renewal: 920000,
        loss_ratio: 0.84,
        claims: [
          {
            visit_type: "Outpatient",
            amount: 12450,
            visits: 28,
            percent_of_total: 45
          },
          {
            visit_type: "Inpatient",
            amount: 8300,
            visits: 3,
            percent_of_total: 30
          },
          {
            visit_type: "Dental",
            amount: 4150,
            visits: 12,
            percent_of_total: 15
          },
          {
            visit_type: "Optical",
            amount: 2760,
            visits: 8,
            percent_of_total: 10
          }
        ],
        activities: [
          {
            description: "Quote Auto-Created",
            date: "2025-05-12T10:30:00",
          },
          {
            description: "Viewed by Sales Owner",
            date: "2025-05-12T11:15:00",
          }
        ]
      };
      */
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch renewal quote details");
    }
  }
);

// Reject quotation
export const rejectQuotation = createAsyncThunk(
  "renewal/rejectQuotation",
  async (accountId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("auth-token");
      const response = await axios({
        method: "PATCH",
        url: `${baseUrl}/quotation/${accountId}/reject`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      // Return the full response data for form population
      return response.data;
    } catch (error) {
      console.error("Error rejecting quotation:", error);
      return rejectWithValue(error.response?.data?.message || "Failed to reject quotation");
    }
  }
);

// Approve quotation
export const approveQuotation = createAsyncThunk(
  "renewal/approveQuotation",
  async (accountId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("auth-token");
      const response = await axios({
        method: "PATCH",
        url: `${baseUrl}/quotation/${accountId}/accept`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      return response.data;
    } catch (error) {
      console.error("Error approving quotation:", error);
      return rejectWithValue(error.response?.data?.message || "Failed to approve quotation");
    }
  }
);

// Create renewal slice
const renewalSlice = createSlice({
  name: "renewal",
  initialState,
  reducers: {
    clearRenewalQuoteDetails: (state) => {
      state.quoteDetails.data = null;
      state.quoteDetails.error = null;
    },
    clearRenewalQuotes: (state) => {
      state.renewalQuotes.data = [];
      state.renewalQuotes.error = null;
    },
    resetQuoteAction: (state) => {
      state.quoteAction = {
        loading: false,
        success: false,
        error: null
      };
    },
    clearRejectedQuoteData: (state) => {
      state.rejectedQuoteData = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all renewal quotes
      .addCase(getAllRenewalQuotes.pending, (state) => {
        state.renewalQuotes.loading = true;
        state.renewalQuotes.error = null;
      })
      .addCase(getAllRenewalQuotes.fulfilled, (state, action) => {
        state.renewalQuotes.loading = false;
        
        console.log("API Response in reducer:", action.payload);
        
        try {
          // Handle the specific response structure: data.result.content
          if (action.payload?.data?.result?.content && Array.isArray(action.payload.data.result.content)) {
            // Get the quotes data
            const quotes = action.payload.data.result.content;
            // Get pagination data from property field if it exists
            const property = action.payload.data.result.property || {};
            const totalItems = property.total_elements || quotes.length;
            const totalPages = property.total_pages || Math.ceil(totalItems / (property.page_size || 10));
            const currentPage = property.page_number || 0;
            
            // Update state
            state.renewalQuotes.data = quotes;
            state.renewalQuotes.totalItems = totalItems;
            state.renewalQuotes.totalPages = totalPages;
            state.renewalQuotes.currentPage = currentPage;
            
            console.log("Processed data:", { 
              quotesCount: quotes.length,
              totalItems,
              totalPages,
              currentPage
            });
          }
          // Fallback to existing handlers if the specific format is not found
          else if (action.payload && action.payload.data) {
            let quotes = [];
            let totalItems = 0;
            let totalPages = 1;
            let currentPage = 0;
            
            // Case 1: Nested result object without content
            if (action.payload.data.result && !action.payload.data.result.content) {
              const result = action.payload.data.result;
              
              // Check if result is an array
              if (Array.isArray(result)) {
                quotes = result;
                totalItems = result.length;
              }
              // Result is an object without content
              else {
                console.warn("Unexpected result structure:", result);
              }
            } 
            // Case 2: Direct array in data
            else if (Array.isArray(action.payload.data)) {
              quotes = action.payload.data;
              totalItems = quotes.length;
            }
            // Case 3: Paginated response in data
            else if (action.payload.data.content && Array.isArray(action.payload.data.content)) {
              quotes = action.payload.data.content;
              totalItems = action.payload.data.totalElements || quotes.length;
              totalPages = action.payload.data.totalPages || Math.ceil(totalItems / 10);
              currentPage = action.payload.data.number || 0;
            }
            // Case 4: Look for any array property
            else {
              const arrayProps = Object.keys(action.payload.data).filter(key => 
                Array.isArray(action.payload.data[key]) && action.payload.data[key].length > 0
              );
              
              if (arrayProps.length > 0) {
                quotes = action.payload.data[arrayProps[0]];
                totalItems = quotes.length;
              } else {
                console.warn("No array data found in response:", action.payload.data);
              }
            }
            
            // Update state with processed data
            state.renewalQuotes.data = quotes;
            state.renewalQuotes.totalItems = totalItems;
            state.renewalQuotes.totalPages = totalPages;
            state.renewalQuotes.currentPage = currentPage;
          } else {
            console.warn("Empty or invalid API response:", action.payload);
            state.renewalQuotes.data = [];
            state.renewalQuotes.totalItems = 0;
            state.renewalQuotes.totalPages = 0;
            state.renewalQuotes.currentPage = 0;
          }
        } catch (error) {
          console.error("Error processing API response:", error);
          state.renewalQuotes.data = [];
          state.renewalQuotes.error = "Error processing server response";
        }
      })
      .addCase(getAllRenewalQuotes.rejected, (state, action) => {
        state.renewalQuotes.loading = false;
        state.renewalQuotes.error = action.payload || "Failed to fetch renewal quotes";
        state.renewalQuotes.data = []; // Clear data on error
      })
      
      // Get renewal quote details
      .addCase(getRenewalQuoteDetails.pending, (state) => {
        state.quoteDetails.loading = true;
        state.quoteDetails.error = null;
      })
      .addCase(getRenewalQuoteDetails.fulfilled, (state, action) => {
        state.quoteDetails.loading = false;
        
        console.log("Quote details API response:", action.payload);
        
        if (action.payload) {
          // Handle the nested result structure
          if (action.payload.result) {
            state.quoteDetails.data = action.payload.result;
          } 
          // Handle direct response object
          else if (typeof action.payload === 'object') {
            state.quoteDetails.data = action.payload;
          } 
          // Handle unexpected response
          else {
            console.warn("Unexpected quote details response format:", action.payload);
            state.quoteDetails.data = null;
            state.quoteDetails.error = "Invalid response format";
          }
        } else {
          console.warn("Empty quote details response");
          state.quoteDetails.data = null;
        }
      })
      .addCase(getRenewalQuoteDetails.rejected, (state, action) => {
        state.quoteDetails.loading = false;
        state.quoteDetails.error = action.payload || "Failed to fetch renewal quote details";
      })
      
      // Reject quotation
      .addCase(rejectQuotation.pending, (state) => {
        state.quoteAction.loading = true;
        state.quoteAction.error = null;
        state.quoteAction.success = false;
      })
      .addCase(rejectQuotation.fulfilled, (state, action) => {
        state.quoteAction.loading = false;
        state.quoteAction.success = true;
        // Store the rejected quote data for form population
        state.rejectedQuoteData = action.payload;
      })
      .addCase(rejectQuotation.rejected, (state, action) => {
        state.quoteAction.loading = false;
        state.quoteAction.error = action.payload || "Failed to reject quotation";
      })
      
      // Approve quotation
      .addCase(approveQuotation.pending, (state) => {
        state.quoteAction.loading = true;
        state.quoteAction.error = null;
        state.quoteAction.success = false;
      })
      .addCase(approveQuotation.fulfilled, (state) => {
        state.quoteAction.loading = false;
        state.quoteAction.success = true;
      })
      .addCase(approveQuotation.rejected, (state, action) => {
        state.quoteAction.loading = false;
        state.quoteAction.error = action.payload || "Failed to approve quotation";
      });
  }
});

export const { clearRenewalQuoteDetails, clearRenewalQuotes, resetQuoteAction, clearRejectedQuoteData } = renewalSlice.actions;
export default renewalSlice.reducer; 